import React, { useEffect, useRef } from 'react';
import { Link } from 'gatsby';
import styled from 'styled-components';

import * as d3 from 'd3';

const IndividualViolin = ({
  data,
  halfSize,
  type,
  width,
  label = '',
  background = true,
}) => {
  const div = useRef(null);

  const size = {
    width: halfSize ? 160 : width,
    height: halfSize ? 120 : 170,
  };

  const margin = {
    left: 20,
    top: 5,
    right: 20,
    bottom: 50,
  };

  const placeAxis = (svg, ax, n, fmt) => {
    svg
      .append('g')
      .attr('transform', `translate(0, ${size.height - margin.bottom + 5})`)
      .style('font-family', 'Helvetica Neue,Helvetica,Arial,sans-serif')
      .style('font-size', '16px')
      .attr('color', '#adadad')
      .call(d3.axisBottom(ax).ticks(n).tickFormat(fmt));
  };

  useEffect(() => {
    if (!div.current) {
      console.log('EXITING');
      return;
    }

    // console.log(data, letter);

    const container = d3
      .select(div.current)
      .style(
        'background-color',
        background && window.innerWidth > 640 && halfSize
          ? '#d3d3d355'
          : 'white',
      );

    container.selectAll('*').remove();

    if (data.key) {
      container
        .selectAll('h3')
        .data([data.key])
        .enter()
        .append('h3')
        .text((d) => d)
        .style('margin', 0)
        .style('padding', '15px 0 0 15px');
    }

    container.style('width', `${size.width}px`);

    const svg = container
      .selectAll('svg')
      .data([0])
      .join('svg')
      .attr('width', size.width)
      .attr('height', size.height);

    if (label !== '') {
      svg
        .append('text')
        .text(label)
        .attr('font-size', '10pt')
        .attr('fill', '#adadad')
        .attr('x', size.width / 2)
        .attr('text-anchor', 'middle')
        .attr('y', size.height - 5);
    }

    const x = d3
      .scaleLinear()
      .domain([0, 4])
      .range([margin.left, size.width - margin.right]);

    const xPNP = d3
      .scaleLinear()
      .domain([0, 1])
      .range([margin.left, size.width - margin.right]);

    const y = d3
      .scaleLinear()
      .domain([-1, 1])
      .range([size.height - margin.bottom, margin.top]);

    svg
      .selectAll('.violinCircs')
      .data(data.values)
      .join('circle')
      .attr('class', 'violinCircs')
      .attr('cx', (d) => (type === 'letter'
        ? x(+d.avgGPA)
        : xPNP(+d.nPNPStudents ? +d.P / +d.nPNPStudents : +d.S / +d.su)))
      .attr('cy', y(0))
      .attr('fill', (d) => (type === 'letter' ? 'black' : type === 'pnp' ? '#08519C' : '#D96942'))
      .attr('fill-opacity', 0.3)
      .attr('r', 0)
      .transition()
      .duration(1000)
      .attr('r', halfSize ? 2 : 4);

    if (type === 'letter') {
      placeAxis(svg, x, 5, (d) => `${d}.0`);
    }
    else {
      placeAxis(svg, xPNP, 3, (d) => `${d * 100}%`);
    }

    const histogram = d3
      .bin()
      .domain([0, 4])
      .thresholds(x.ticks(15))
      .value((d) => d);

    const histogramPNP = d3
      .bin()
      .domain([0, 1])
      .thresholds(xPNP.ticks(10))
      .value((d) => d);

    const plotData = data.values.filter((d) => {
      if (type === 'letter') {
        return +d.avgGPA !== -1;
      }
      if (type === 'pnp') {
        return +d.nPNPStudents !== -1;
      }
      if (type === 'su') {
        return +d.su !== -1;
      }
    });

    if (plotData.length === 1) {
      return [];
    }

    const violinData = (() => {
      if (type === 'letter') {
        const bins = d3.rollup(plotData, (d) => {
          const agpa = d.map((d) => +d.avgGPA);
          return histogram(agpa);
        });

        const max = Math.max(...bins.map((d) => d.length));

        return [
          bins.map((d) => ({
            val: d.length / max,
            x0: x(d.x0),
            x1: x(d.x1),
          })),
        ];
      }
      const bins = d3.rollup(plotData, (d) => {
        const agpa =
          type === 'pnp'
            ? d.map((d) => +d.P / +d.nPNPStudents)
            : d.map((d) => +d.S / +d.su);
        return histogramPNP(agpa);
      });
      const max = Math.max(...bins.map((d) => d.length));
      return [
        bins.map((d) => ({
          val: d.length / max,
          x0: xPNP(d.x0),
          x1: xPNP(d.x1),
        })),
      ];
    })();

    svg
      .selectAll('.violinArea')
      .data(violinData)
      .join('path')
      .attr(
        'fill',
        type === 'letter' ? 'black' : type === 'pnp' ? '#08519C' : '#D96942',
      )
      .attr('fill-opacity', 0.2)
      .attr('class', 'violin')
      .attr(
        'd',
        d3
          .area()
          .y0(y(0))
          .y1(y(0))
          .x((d) => d.x0)
          .curve(d3.curveCatmullRom.alpha(1)),
      )
      .transition()
      .duration(1000)
      .attr(
        'd',
        d3
          .area()
          .y0((d) => y(-d.val))
          .y1((d) => y(d.val))
          .x((d) => d.x0)
          .curve(d3.curveCatmullRom.alpha(1)),
      );
  }, [data, div, size.width]);

  return <div ref={div} />;
};

const Container = styled.div`
  display: flex;
  justify-content: center;
`;

const ViolinContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  max-width: 640px;
`;

const ViolinLink = styled(Link)`
  color: black;
  text-decoration: none;
`;

const Center = styled.div`
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-start;
`;

const ConditionalLink = ({ children, to, link }) => {
  if (link) {
    return <ViolinLink to={to}>{children}</ViolinLink>;
  }
  return children;
};

const Violin = ({
  data, halfSize, link, width,
}) => {
  let background = true;
  return (
    <Container>
      <ViolinContainer>
        {data.map((d) => {
          const letters =
            d.values.find((d) => +d.nLetterStudents > 0) !== undefined;
          const pnp = d.values.find((d) => +d.nPNPStudents > 0) !== undefined;
          const su = d.values.find((d) => +d.su > 0) !== undefined;
          const bg1 = !background && letters;
          const bg2 = ((!bg1 && letters) || (!background && !letters)) && pnp;
          const bg3 =
            ((!bg2 && pnp)
              || (!background && !letters && !pnp)
              || (!bg1 && letters && !pnp))
            && su;

          background = (bg1 && !pnp && !su) || (bg2 && !su) || bg3;
          return (
            <Center key={d.key}>
              {letters ? (
                <ConditionalLink to={`/courses?q="${d.key}"`} link={link}>
                  <IndividualViolin
                    data={{
                      key: d.key,
                      values: d.values.filter((d) => +d.nLetterStudents > 0),
                    }}
                    halfSize={halfSize}
                    type="letter"
                    width={width}
                    label="Average GPAs"
                    background={bg1}
                  />
                </ConditionalLink>
              ) : null}
              {pnp ? (
                <ConditionalLink to={`/courses?q="${d.key}"`} link={link}>
                  <IndividualViolin
                    data={{
                      key: halfSize || !letters ? d.key : null,
                      values: d.values.filter((d) => +d.nPNPStudents > 0),
                    }}
                    width={width}
                    halfSize={halfSize}
                    type="pnp"
                    label="Average Passing %"
                    background={bg2}
                  />
                </ConditionalLink>
              ) : null}
              {su ? (
                <ConditionalLink to={`/courses?q="${d.key}"`} link={link}>
                  <IndividualViolin
                    data={{
                      key: halfSize || (!letters && !pnp) ? d.key : null,
                      values: d.values.filter((d) => +d.su > 0),
                    }}
                    width={width}
                    halfSize={halfSize}
                    type="su"
                    label="Average Satisfactory %"
                    background={bg3}
                  />
                </ConditionalLink>
              ) : null}
            </Center>
          );
        })}
      </ViolinContainer>
    </Container>
  );
};

export default Violin;
